import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  Building2, Users, X, Edit2, BarChart3,
  UserPlus, Activity
} from "lucide-react";

// ========================
// CREATE CLINIC MODAL
// ========================
export function CreateClinicModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    address: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Le nom de la clinique est requis");
      return;
    }
    setLoading(true);
    try {
      await api.post("/clinics", form);
      toast.success("Clinique créée avec succès");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="create-clinic-modal">
        <div className="p-6 border-b bg-emerald-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              Nouvelle clinique
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nom de la clinique *</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Clinique HANA Rabat" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="clinic-name" required />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Adresse</label>
            <input type="text" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Avenue Mohammed V, Rabat" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="clinic-address" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Téléphone</label>
            <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="0522000000" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="clinic-phone" />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Annuler</button>
            <button type="submit" disabled={loading} data-testid="submit-create-clinic" className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium">{loading ? "..." : "Créer"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ========================
// EDIT CLINIC MODAL
// ========================
export function EditClinicModal({ clinic, onClose, onUpdated }) {
  const [form, setForm] = useState({
    name: clinic.name || "",
    address: clinic.address || "",
    phone: clinic.phone || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/clinics/${clinic.id}`, form);
      toast.success("Clinique mise à jour");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="edit-clinic-modal">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800">Modifier la clinique</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nom</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Adresse</label>
            <input type="text" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Téléphone</label>
            <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium">{loading ? "..." : "Enregistrer"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ========================
// CREATE ADMIN MODAL
// ========================
export function CreateAdminModal({ clinic, onClose, onCreated }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [initialPassword, setInitialPassword] = useState(null);
  const [createdAdminLabel, setCreatedAdminLabel] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/clinics/${clinic.id}/admin`, form);
      setInitialPassword(res.data?.initialPassword || form.password);
      setCreatedAdminLabel(`${form.firstName} ${form.lastName}`.trim());
      toast.success("Admin créé avec succès");
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="create-admin-modal">
        <div className="p-6 border-b bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-amber-600" />
                Créer un admin
              </h2>
              <p className="text-sm text-slate-500 mt-1">Pour: {clinic.name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>

        {initialPassword ? (
          <div className="p-6 space-y-4">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="clinic-admin-initial-password-card">
              <p className="text-sm font-semibold text-amber-900">Mot de passe temporaire Admin Clinique</p>
              {createdAdminLabel && <p className="text-sm text-amber-700 mt-1">{createdAdminLabel}</p>}
              <p className="text-xl font-black tracking-wide text-amber-800 mt-2" data-testid="clinic-admin-initial-password-value">{initialPassword}</p>
              <p className="text-xs text-amber-700 mt-2">À transmettre à l’admin. Changement obligatoire à la première connexion, puis création du code Hana.</p>
            </div>
            <button type="button" onClick={onCreated} className="w-full py-3 bg-amber-600 text-white rounded-xl font-semibold" data-testid="clinic-admin-initial-password-done-button">
              Accès transmis — fermer
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Prénom *</label>
              <input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="admin-firstName" required />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nom *</label>
              <input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="admin-lastName" required />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Email *</label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="admin@clinique.ma" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="admin-email" required />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Mot de passe initial</label>
            <input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" data-testid="admin-password" />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Annuler</button>
            <button type="submit" disabled={loading} data-testid="submit-create-admin" className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium">{loading ? "..." : "Créer l'admin"}</button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

// ========================
// CLINIC STATS MODAL
// ========================
export function ClinicStatsModal({ clinic, onClose }) {
  const stats = clinic.stats;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" data-testid="clinic-stats-modal">
        <div className="p-6 border-b bg-emerald-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                Statistiques
              </h2>
              <p className="text-sm text-slate-500">{clinic.name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Patients</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-emerald-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-emerald-600">{stats?.patients?.total ?? 0}</p>
                <p className="text-xs text-slate-500">Total actifs</p>
              </div>
              <div className="p-4 bg-red-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-red-600">{stats?.patients?.critical ?? 0}</p>
                <p className="text-xs text-slate-500">Critiques</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> Équipe</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-slate-50 rounded-xl text-center">
                <p className="text-xl font-bold text-slate-700">{stats?.users?.total ?? 0}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl text-center">
                <p className="text-xl font-bold text-blue-600">{stats?.users?.doctors ?? 0}</p>
                <p className="text-xs text-slate-500">Médecins</p>
              </div>
              <div className="p-3 bg-pink-50 rounded-xl text-center">
                <p className="text-xl font-bold text-pink-600">{stats?.users?.nurses ?? 0}</p>
                <p className="text-xs text-slate-500">IDE</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Activity className="w-4 h-4" /> Activité (30 derniers jours)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-emerald-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-emerald-600">{stats?.activity?.checkins30d ?? 0}</p>
                <p className="text-xs text-slate-500">Check-ins</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-amber-600">{stats?.activity?.alerts30d ?? 0}</p>
                <p className="text-xs text-slate-500">Alertes</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t">
          <button onClick={onClose} className="w-full py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200">Fermer</button>
        </div>
      </div>
    </div>
  );
}
